import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
// ----------------------------------------------------------
export interface ChartDataset {
    /**
     * Be able to change type of the dataset and have mix graphs
     */
    type?: "line" | "bar" | "bubble" | "scatter";
    /**
     *  The name of the dataset 
     */
    label: string;
    /**
     * A list with the numeric values for each label. _It can contain null values._
     */
    data: number[];

    /**
     * The ID of the x/y axis to plot this dataset on.
     */
    yAxisID?: string;
    xAxisID?: string;

    /**
     * Specify border's thickness 
     */
    borderWidth?: number | number[];

    /**
     * The color of the line
     */
    borderColor?: string | string[];
    borderDash?: number[];

    /**
     * The fill color under the line in rgba format (ex. "rgba(0,0,0,1)")
     */
    backgroundColor?: string | string[];

    /**
     * The border color for points
     */
    pointBorderColor?: string | string[];

    /**
     * The fill color for points
     */
    pointBackgroundColor?: string | string[];

    /**
     * Point border color when hovered
     */
    pointHoverBorderColor?: string | string[];

    /**
     * Point background color when hovered
     */
    pointHoverBackgroundColor?: string | string[];
    pointRadius?: number | number[];
    pointHitRadius?: number | number[];
    pointHoverRadius?: number | number[];

    /**
     * Specifies whether to interpolate over missing values of not.
     */
    spanGaps?: boolean;

    /**
     * Specifies if the line is shown as a stepped line
     */
    steppedLine?: boolean;

    /**
     * Specifies whether to hide line from initial view. 
     * The user would have possibility to unhide the dataset from the legend. 
     * his option is useful when many datasets are displayed.
     */
    hidden?: boolean;
}
// ----------------------------------------------------------
export interface ChartPortData {
    /**
     * A list with all the labels of the chart
     */
    labels?: string[];

    /**
     * A list of objects with all the groups of data.
     */
    datasets: ChartDataset[];

    /**
     * A list with all the x axis labels of the chart
     */
    xLabels?: string[] | number[];

    /**
     * A list with all the y axis labels of the chart. This need to set the yAxisID fo dataset to `cat-y-left` or `cat-y-right`
     */
    yLabels?: string[] | number[];

    /**
     * Configuration of x axes.
     */
    xAxes?: Chart.CommonAxe[];

    /**
     * Configuration of y axes.
     */
    yAxes?: Chart.CommonAxe[];
}
// ----------------------------------------------------------
export interface OutputChartData {
    Dataset: string;
    Label: string;
    Value: number;
}
// ----------------------------------------------------------
export function ChartsJsonSchemeValidator() {
    const axesSchema = {
        type: 'array',
        items: {
            type: 'object',
            properties: {
                id: { type: 'string' },
                type: {
                    enum: [
                        'time',
                        'linear',
                        'category',
                        'logarithmic',
                        'radialLinear'
                    ]
                },
                position: {
                    enum: [
                        'top',
                        'left',
                        'right',
                        'bottom'
                    ]
                },
                display: { type: 'boolean' }
            },
            required: ['id']
        },
        minItems: 1
    };
    return JsonSchemaValidator.GetInstance("ChartsJsonSchemeValidator", {
        title: "LineChartPortData",
        type: "object",
        properties: {
            labels: {
                type: "array",
                items: {
                    type: ["string", "number"]
                },
                minItems: 1
            },
            xLabels: {
                type: "array",
                items: {
                    type: ["string", "number"]
                },
                minItems: 1
            },
            yLabels: {
                type: "array",
                items: {
                    type: ["string", "number"]
                },
                minItems: 1
            },
            xAxes: axesSchema,
            yAxes: axesSchema,
            datasets: {
                type: "array",
                items: {
                    type: "object",
                    // TODO: Define all objects
                    properties: {
                        label: { type: ["string", "array"] },
                        data: {
                            type: "array", items: {
                                oneOf: [
                                    { type: ["string", "number", "null"] },
                                    {
                                        type: ["object"],
                                        properties: {
                                            x: { type: ["string", "number"] },
                                            y: { type: ["string", "number"] },
                                            r: { type: ["string", "number"] }
                                        }
                                    }
                                ]
                            }
                        },
                        type: { enum: ['line', 'bar', 'bubble', 'scatter'] }
                    },
                    required: ["label", "data"]
                },
                minItems: 1
            }
        },
        required: ["datasets"]
    });
}
// ----------------------------------------------------------
export const chartDateRangeValidator = () => JsonSchemaValidator.GetInstance('chartDateRangeValidator', {
    type: 'object',
    properties: {
        StartDate: { type: 'string' },
        EndDate: { type: 'string' }
    }
});
// ----------------------------------------------------------
export interface ChartDateRange {
    StartDate: string;
    EndDate: string;
}
// ----------------------------------------------------------